import { TinySlider } from '@/components';
import { useToggle } from '@/hooks';
import { currency, useLayoutContext } from '@/states';
import { splitArray } from '@/utils';
import { getHotelImage } from '@/utils/imageUrl';
import { Button, Card, CardBody, CardHeader, Col, Image, Modal, ModalBody, ModalHeader, Row } from 'react-bootstrap';
import { renderToString } from 'react-dom/server';
import { BsArrowLeft, BsArrowRight, BsEyeFill } from 'react-icons/bs';
import { FaCheckCircle } from 'react-icons/fa';
import { Link } from '@/compat/react-router-dom';
import { useHotelEngineSell } from '@/hooks';
import '@/assets/css/vendor/tiny-slider.css';

const amenities = ['Swimming Pool', 'Spa', 'Kids Play Area', 'Gym', 'Tv', 'Mirror', 'Ac', 'Slippers'];

const RoomCard = ({ room }) => {
  const { isOpen, toggle } = useToggle();
  const { dir } = useLayoutContext();
  const { sellable } = useHotelEngineSell();

  const roomSliderSettings = {
    nested: 'inner',
    autoplay: false,
    controls: true,
    autoplayButton: false,
    autoplayButtonOutput: false,
    controlsText: [renderToString(<BsArrowLeft size={16} />), renderToString(<BsArrowRight size={16} />)],
    arrowKeys: true,
    items: 1,
    nav: false,
    autoplayDirection: dir === 'ltr' ? 'forward' : 'backward'
  };

  const chunk_size = 2;
  const amenitiesChunks = splitArray(amenities, chunk_size);

  // Get room data
  const roomName = room?.name || room?.room_name || 'Room';
  const roomPrice = room?.price || room?.min_price || 0;
  const roomPhotoUrls = Array.isArray(room?.photos) ? room.photos.map(photo => photo?.url).filter(Boolean) : [];
  const roomImages = roomPhotoUrls.length > 0
    ? roomPhotoUrls
    : Array.isArray(room?.images)
      ? room.images
      : room?.image
        ? [room.image]
        : [];
  const roomDescription = room?.description_html || room?.description || '';
  const roomFeatures = room?.features || ['Standard Room', '1 Bed', '2 Guests'];
  const hasHtmlDescription = typeof roomDescription === 'string' && roomDescription.includes('<');

  // Helper to get image URL - handles full URLs (with localhost replacement) and relative paths
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // Use getHotelImage: handles full URLs (replaceLocalhostUrl), pictures/hotels/ (media API), and uploads
    return getHotelImage(imagePath, null);
  };

  const displayImages = roomImages.length > 0 
    ? roomImages.map(getImageUrl).filter(Boolean)
    : []; // Use placeholder if no images

  return (
    <Card className="shadow p-3">
      <Row className="g-4">
        <Col md={5} className="position-relative">
          {displayImages.length > 0 ? (
            <div className="tiny-slider arrow-round arrow-xs arrow-dark overflow-hidden rounded-2">
              <TinySlider settings={roomSliderSettings}>
                {displayImages.map((image, idx) => (
                  <div key={idx}>
                    <Image src={image} alt="Room image" loading="eager" className="w-100" style={{ height: '250px', objectFit: 'cover' }} />
                  </div>
                ))}
              </TinySlider>
            </div>
          ) : (
            <div className="bg-light rounded-2 d-flex align-items-center justify-content-center" style={{ height: '250px' }}>
              <p className="text-muted mb-0">No image available</p>
            </div>
          )}
          <Link to="" className="btn btn-link text-decoration-underline p-0 mb-0 mt-1 items-center" onClick={toggle}>
            <BsEyeFill className="me-1" />
            View more details
          </Link>
        </Col>
        <Col md={7}>
          <div className="card-body d-flex flex-column h-100 p-0">
            <h5 className="card-title">
              <Link to="">{roomName}</Link>
            </h5>
            <ul className="nav nav-divider mb-2">
              {roomFeatures.map((feature, idx) => (
                <li key={idx} className="nav-item">
                  {feature}
                </li>
              ))}
            </ul>

            <p className="text-muted small mb-3">Brochure room. Live rates open only when hotel selling is certified.</p>

            <div className="d-sm-flex justify-content-sm-between align-items-center mt-auto">
              <div className="d-flex align-items-center">
                <h5 className="fw-bold mb-0 me-1">
                  {currency}
                  {roomPrice}
                </h5>
                <span className="mb-0 me-2">/day</span>
              </div>
              <div className="mt-3 mt-sm-0">
                {sellable ? (
                  <Button as={Link} to="/hotels/search" variant="primary" size="sm" className="mb-0">
                    Search dates
                  </Button>
                ) : (
                  <Button as={Link} to="/hotels" variant="outline-primary" size="sm" className="mb-0">
                    Hotel catalogue
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Modal show={isOpen} onHide={toggle} className="fade">
            <ModalHeader className="p-3">
              <h5 className="modal-title mb-0" id="roomDetailLabel">
                Room detail
              </h5>
              <button type="button" onClick={toggle} className="btn-close" />
            </ModalHeader>
            <ModalBody className="p-0">
              <Card className="bg-transparent p-3">
                {displayImages.length > 0 && (
                  <div className="tiny-slider arrow-round arrow-dark overflow-hidden rounded-2">
                    <TinySlider settings={roomSliderSettings} className="rounded-2 overflow-hidden">
                      {displayImages.map((image, idx) => (
                        <div key={idx}>
                          <Image src={image} className="rounded-2 w-100" alt="Room image" loading="eager" style={{ height: '300px', objectFit: 'cover' }} />
                        </div>
                      ))}
                    </TinySlider>
                  </div>
                )}
                <CardHeader className="bg-transparent pb-0">
                  <h3 className="card-title mb-0">{roomName}</h3>
                </CardHeader>
                <CardBody>
                  {hasHtmlDescription ? (
                    <div dangerouslySetInnerHTML={{ __html: roomDescription }} />
                  ) : (
                    <p>{roomDescription || 'Comfortable room with modern amenities.'}</p>
                  )}
                  <h5 className="mb-0">Amenities</h5>
                  {amenitiesChunks.map((chunk, idx) => (
                    <Row key={idx}>
                      {chunk.map((item, itemIdx) => (
                        <Col key={itemIdx} md={6}>
                          <ul className="list-group list-group-borderless mt-2 mb-0">
                            <li className="list-group-item d-flex mb-0">
                              <FaCheckCircle className="text-success me-2" />
                              <span className="h6 fw-light mb-0">{item}</span>
                            </li>
                          </ul>
                        </Col>
                      ))}
                    </Row>
                  ))}
                </CardBody>
              </Card>
            </ModalBody>
          </Modal>
        </Col>
      </Row>
    </Card>
  );
};

export default RoomCard;

